import { Button, Divider, Stack, Typography } from "@mui/material";
import type { DeliveryFilterSchemaType } from "@schemas/DeliveryForm/DeliveryFilterSchema";
import type { DeliveryType } from "@types_def/models/delivery.types";
import { deliveryTypesForUiFilter } from "@utils/constants/DeliveryType/deliveryTypeAttributes";
import { getDeliveryTypeColor } from "@utils/functions/Delivery/DeliveryType/deliveryTypeAttributes";
import { enumToStr } from "@utils/strEnum";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

type DeliveryTableTypeFilterProps = {
  handleTypeChange?: (nextType: DeliveryType) => void;
  tableType?: DeliveryType;
  handleTypeClear?: () => void;
};

function DeliveryTableTypeFilter({
  handleTypeChange,
  tableType,
  handleTypeClear,
}: DeliveryTableTypeFilterProps) {
  const { t } = useTranslation();
  const methods = useFormContext<DeliveryFilterSchemaType>();
  const deliveryTypes = deliveryTypesForUiFilter;

  const handleLegacyToggle = (deliveryType: DeliveryType) => {
    if (!handleTypeChange) return;

    if (tableType === deliveryType) {
      handleTypeClear?.();
      return;
    }

    handleTypeChange(deliveryType);
  };

  const isSelected = (deliveryType: DeliveryType) => {
    if (methods) {
      return Boolean(methods.watch(`deliveryType.${deliveryType}`));
    }

    return tableType === deliveryType;
  };

  const handleFormToggle = (deliveryType: DeliveryType) => {
    if (!methods) return;

    methods.setValue(`deliveryType.${deliveryType}`, !isSelected(deliveryType));
  };

  return (
    <>
      <Typography variant="body2" letterSpacing={2} fontWeight={600}>
        Delivery Type
      </Typography>
      <Divider />
      <Stack direction="row" sx={{ flexWrap: "wrap" }} p={1} gap={1}>
        {deliveryTypes.map((type) => (
          <Button
            size="small"
            variant={isSelected(type) ? "contained" : "outlined"}
            onClick={() => {
              if (methods) {
                handleFormToggle(type);
                return;
              }

              handleLegacyToggle(type);
            }}
            key={`delivery-table-type-filter-${type}`}
          >
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: getDeliveryTypeColor(type as DeliveryType),
                marginRight: 5,
              }}
            ></span>
            {t(`layouts.sub-headers.deliveries.${enumToStr(type)}`)}
          </Button>
        ))}
      </Stack>
    </>
  );
}

export default DeliveryTableTypeFilter;
